import { useState, useCallback } from 'react';
import { Modal } from '../../../../components/base/Modal';
import { Button } from '../../../../components/base/Button';
import ImageUpload from '../../../../components/base/ImageUpload';
import TagInput from '../../../../components/base/TagInput';
import { groupsApi } from '../../../../lib/api';

interface GroupSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: {
    id: string;
    name: string;
    description: string;
    category: string;
    privacy: string;
    rules: string[];
    cover_image: string;
  };
  onSave: (data: any) => void;
  onDelete: () => void;
  userRole: string;
  initialTab?: 'general' | 'topics' | 'rules' | 'members' | 'danger';
}

const GroupSettingsModal = ({ isOpen, onClose, group, onSave, onDelete, userRole, initialTab = 'general' }: GroupSettingsModalProps) => {
  const [activeTab, setActiveTab] = useState<'general' | 'topics' | 'rules' | 'welcome' | 'members' | 'danger'>(initialTab as any);
  const [formData, setFormData] = useState({
    name: group.name,
    description: group.description,
    category: group.category,
    privacy: group.privacy,
    cover_image: group.cover_image,
  });
  const [rules, setRules] = useState<string[]>(group.rules || []);
  const [welcomeMessage, setWelcomeMessage] = useState<string>((group as any).welcome_message || '');
  const [newRule, setNewRule] = useState('');
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);
  const [editingRuleText, setEditingRuleText] = useState('');
  const [topics, setTopics] = useState<string[]>([]);
  const [transferUserId, setTransferUserId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const categories = ['Entertainment', 'Production', 'Genre', 'Regional', 'Professional'];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Group name must be at least 3 characters';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 20) {
      newErrors.description = 'Description must be at least 20 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (activeTab !== 'rules' && activeTab !== 'welcome' && !validateForm()) return;
    setIsSaving(true);
    setApiError(null);
    try {
      await onSave({ ...formData, rules, welcome_message: welcomeMessage });
      if (activeTab === 'topics') {
        const topicsResponse = await groupsApi.updateTopics(group.id, topics);
        if (!topicsResponse.success) {
          throw new Error(topicsResponse.error || 'Failed to update topics');
        }
      }
      onClose();
    } catch (error: any) {
      console.error('Error saving settings:', error);
      setApiError(error.message || 'Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const addRule = () => {
    if (newRule.trim() && rules.length < 20) {
      setRules([...rules, newRule.trim()]);
      setNewRule('');
    }
  };

  const removeRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index));
    if (editingRuleIndex === index) {
      setEditingRuleIndex(null);
      setEditingRuleText('');
    }
  };

  const startEditRule = (index: number) => {
    setEditingRuleIndex(index);
    setEditingRuleText(rules[index]);
  };

  const saveEditRule = () => {
    if (editingRuleIndex !== null && editingRuleText.trim()) {
      const updated = [...rules];
      updated[editingRuleIndex] = editingRuleText.trim();
      setRules(updated);
    }
    setEditingRuleIndex(null);
    setEditingRuleText('');
  };

  const cancelEditRule = () => {
    setEditingRuleIndex(null);
    setEditingRuleText('');
  };

  // Drag and drop for rules reordering
  const handleDragStart = useCallback((index: number) => {
    setDragIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      const updated = [...rules];
      const [removed] = updated.splice(dragIndex, 1);
      updated.splice(dragOverIndex, 0, removed);
      setRules(updated);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  }, [dragIndex, dragOverIndex, rules]);

  const moveRule = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= rules.length) return;
    const updated = [...rules];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setRules(updated);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this group? This action cannot be undone. All posts, members, and data will be permanently removed.')) {
      onDelete();
      onClose();
    }
  };

  const handleTransferOwnership = async () => {
    if (!transferUserId.trim()) {
      setApiError('Please enter a user ID');
      return;
    }
    if (!window.confirm('Are you sure you want to transfer ownership? You will lose all owner privileges.')) return;
    setIsSaving(true);
    setApiError(null);
    try {
      const response = await groupsApi.transferOwnership(group.id, transferUserId);
      if (!response.success) throw new Error(response.error || 'Failed to transfer ownership');
      onClose();
      window.location.reload();
    } catch (error: any) {
      console.error('Error transferring ownership:', error);
      setApiError(error.message || 'Failed to transfer ownership.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = (url: string) => {
    setFormData({ ...formData, cover_image: url });
  };

  const tabItems = [
    { key: 'general', label: 'General', icon: 'ri-settings-3-line' },
    { key: 'rules', label: 'Rules', icon: 'ri-shield-check-line' },
    { key: 'welcome', label: 'Welcome', icon: 'ri-chat-smile-3-line' },
    { key: 'topics', label: 'Topics', icon: 'ri-hashtag' },
    ...(userRole === 'owner' ? [
      { key: 'members', label: 'Ownership', icon: 'ri-exchange-line' },
      { key: 'danger', label: 'Danger Zone', icon: 'ri-error-warning-line' },
    ] : []),
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Group Settings" size="lg">
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-5 overflow-x-auto -mx-1 px-1">
        {tabItems.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`px-4 py-2.5 font-medium text-sm transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
              activeTab === tab.key
                ? tab.key === 'danger' ? 'text-red-600 border-b-2 border-red-600' : 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className={`${tab.icon} text-base`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {apiError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {apiError}
        </div>
      )}

      <div className="max-h-[58vh] overflow-y-auto pr-1">
        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Group Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setErrors({ ...errors, name: '' }); }}
                maxLength={100}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm ${errors.name ? 'border-red-500' : 'border-gray-200'}`}
              />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => { setFormData({ ...formData, description: e.target.value }); setErrors({ ...errors, description: '' }); }}
                rows={4}
                maxLength={500}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none text-sm ${errors.description ? 'border-red-500' : 'border-gray-200'}`}
              />
              <div className="flex justify-between mt-1">
                {errors.description && <p className="text-xs text-red-500">{errors.description}</p>}
                <p className="text-xs text-gray-400 ml-auto">{formData.description.length}/500</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Cover Image</label>
              <ImageUpload onImageUpload={handleImageUpload} currentImage={formData.cover_image} aspectRatio="16:9" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer text-sm"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Privacy</label>
                <select
                  value={formData.privacy}
                  onChange={(e) => setFormData({ ...formData, privacy: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer text-sm"
                >
                  <option value="public">Public - Anyone can join</option>
                  <option value="private">Private - Requires approval</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Rules Tab */}
        {activeTab === 'rules' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Group Rules</h3>
                <p className="text-xs text-gray-500 mt-0.5">Set guidelines for your community. Drag to reorder.</p>
              </div>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">{rules.length}/20</span>
            </div>

            {/* Rules List */}
            {rules.length > 0 && (
              <div className="space-y-2">
                {rules.map((rule, index) => (
                  <div
                    key={`rule-${index}-${rule.substring(0, 10)}`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`group flex items-start gap-2 p-3 rounded-lg border transition-all ${
                      dragOverIndex === index && dragIndex !== index
                        ? 'border-teal-400 bg-teal-50'
                        : dragIndex === index
                          ? 'opacity-50 border-gray-200 bg-gray-50'
                          : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                    }`}
                  >
                    {/* Drag Handle */}
                    <div className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 mt-0.5">
                      <i className="ri-draggable text-lg"></i>
                    </div>

                    {/* Rule Number */}
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-xs font-bold mt-0.5">
                      {index + 1}
                    </span>

                    {/* Rule Content */}
                    {editingRuleIndex === index ? (
                      <div className="flex-1">
                        <input
                          type="text"
                          value={editingRuleText}
                          onChange={(e) => setEditingRuleText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEditRule();
                            if (e.key === 'Escape') cancelEditRule();
                          }}
                          maxLength={200}
                          className="w-full px-3 py-1.5 border border-teal-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                          autoFocus
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <button onClick={saveEditRule} className="text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap">
                            <i className="ri-check-line mr-0.5"></i> Save
                          </button>
                          <button onClick={cancelEditRule} className="text-xs text-gray-500 hover:text-gray-700 font-medium cursor-pointer whitespace-nowrap">
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <span className="flex-1 text-sm text-gray-700 leading-relaxed">{rule}</span>
                    )}

                    {/* Actions */}
                    {editingRuleIndex !== index && (
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <button
                          onClick={() => moveRule(index, 'up')}
                          disabled={index === 0}
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <i className="ri-arrow-up-s-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => moveRule(index, 'down')}
                          disabled={index === rules.length - 1}
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <i className="ri-arrow-down-s-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => startEditRule(index)}
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors cursor-pointer"
                          title="Edit"
                        >
                          <i className="ri-pencil-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => removeRule(index)}
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {rules.length === 0 && (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-shield-check-line text-xl text-teal-500"></i>
                </div>
                <p className="text-sm text-gray-600 font-medium mb-1">No rules yet</p>
                <p className="text-xs text-gray-500">Add rules to set expectations for your community</p>
              </div>
            )}

            {/* Add New Rule */}
            {rules.length < 20 && (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newRule}
                  onChange={(e) => setNewRule(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addRule()}
                  placeholder="Type a new rule and press Enter..."
                  maxLength={200}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                />
                <Button onClick={addRule} variant="primary" disabled={!newRule.trim()}>
                  <i className="ri-add-line mr-1"></i> Add
                </Button>
              </div>
            )}

            {/* Quick Rule Templates */}
            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 mb-2">Quick add common rules</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  'Be respectful and kind to all members',
                  'No spam or self-promotion',
                  'Stay on topic with group discussions',
                  'No spoilers without proper warnings',
                  'Use appropriate language at all times',
                  'No harassment or bullying',
                  'Credit original creators when sharing content',
                  'Report rule violations to moderators',
                ].filter((r) => !rules.includes(r)).slice(0, 4).map((template) => (
                  <button
                    key={template}
                    onClick={() => {
                      if (rules.length < 20) {
                        setRules([...rules, template]);
                      }
                    }}
                    disabled={rules.length >= 20}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-teal-50 hover:text-teal-700 text-gray-600 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-add-line mr-0.5"></i> {template.length > 40 ? template.substring(0, 40) + '...' : template}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Welcome Message Tab */}
        {activeTab === 'welcome' && (
          <div className="space-y-5">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-semibold text-gray-900">Welcome Message</h3>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                New members will see this message when they first join your group. Leave empty to disable.
              </p>
            </div>

            {/* Preview */}
            {welcomeMessage.trim() && (
              <div className="border border-teal-200 rounded-xl overflow-hidden bg-teal-50/30">
                <div className="px-4 py-2.5 bg-teal-50 border-b border-teal-100 flex items-center gap-2">
                  <i className="ri-eye-line text-teal-600 text-sm"></i>
                  <span className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Preview</span>
                </div>
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-chat-smile-3-fill text-teal-500"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Message from the group</p>
                      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{welcomeMessage}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Editor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Message Content
              </label>
              <textarea
                value={welcomeMessage}
                onChange={(e) => setWelcomeMessage(e.target.value)}
                rows={6}
                maxLength={500}
                placeholder="Welcome to our group! 🎉\n\nWe're thrilled to have you join our community. Feel free to introduce yourself and join the conversations..."
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none text-sm leading-relaxed"
              />
              <div className="flex justify-between mt-1.5">
                <p className="text-xs text-gray-400">
                  {welcomeMessage.trim() ? 'New members will see this after joining' : 'No welcome message set'}
                </p>
                <p className="text-xs text-gray-400">{welcomeMessage.length}/500</p>
              </div>
            </div>

            {/* Quick Templates */}
            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 mb-2.5">Quick templates</p>
              <div className="space-y-2">
                {[
                  {
                    label: 'Friendly Welcome',
                    text: `Welcome to ${group.name}! 🎉\n\nWe're thrilled to have you join our community. Feel free to introduce yourself, explore our discussions, and don't hesitate to share your thoughts.\n\nCheck out the group rules and have fun!`,
                  },
                  {
                    label: 'Professional',
                    text: `Welcome to ${group.name}.\n\nThank you for joining our community. Please take a moment to review our group rules and guidelines before posting.\n\nWe look forward to your contributions!`,
                  },
                  {
                    label: 'Casual & Fun',
                    text: `Hey there, welcome aboard! 👋\n\nSo glad you found us! Jump right in — start a discussion, reply to a thread, or just say hi. This is your space too now.\n\nEnjoy! 🚀`,
                  },
                ].map((template) => (
                  <button
                    key={template.label}
                    onClick={() => setWelcomeMessage(template.text)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-teal-200 hover:bg-teal-50/50 transition-all cursor-pointer text-left group"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-100 group-hover:bg-teal-100 flex items-center justify-center flex-shrink-0 transition-colors">
                      <i className="ri-file-text-line text-gray-400 group-hover:text-teal-500 text-sm transition-colors"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700">{template.label}</p>
                      <p className="text-xs text-gray-400 truncate">{template.text.split('\n')[0]}</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-teal-400 transition-colors"></i>
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Button */}
            {welcomeMessage.trim() && (
              <button
                onClick={() => setWelcomeMessage('')}
                className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-600 transition-colors cursor-pointer"
              >
                <i className="ri-delete-bin-line text-sm"></i>
                Clear welcome message
              </button>
            )}
          </div>
        )}

        {/* Topics Tab */}
        {activeTab === 'topics' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Featured Topics</label>
              <TagInput tags={topics} onChange={setTopics} placeholder="Add topics (e.g., drama, comedy, action)..." maxTags={10} />
              <p className="mt-2 text-xs text-gray-500">Add up to 10 topics to help people find your group</p>
            </div>
          </div>
        )}

        {/* Ownership Tab */}
        {activeTab === 'members' && userRole === 'owner' && (
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <h3 className="text-base font-semibold text-orange-900 mb-2 flex items-center gap-2">
                <i className="ri-exchange-line"></i>
                Transfer Ownership
              </h3>
              <p className="text-sm text-orange-700 mb-4">
                Transfer ownership to another member. You will become an admin and lose owner privileges.
              </p>
              <div className="space-y-3">
                <input
                  type="text"
                  value={transferUserId}
                  onChange={(e) => setTransferUserId(e.target.value)}
                  placeholder="Enter user ID of new owner..."
                  className="w-full px-4 py-2.5 border border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                />
                <Button
                  onClick={handleTransferOwnership}
                  variant="outline"
                  disabled={isSaving}
                  className="border-orange-500 text-orange-600 hover:bg-orange-50 whitespace-nowrap"
                >
                  {isSaving ? (
                    <><i className="ri-loader-4-line animate-spin mr-2"></i>Transferring...</>
                  ) : (
                    <><i className="ri-exchange-line mr-2"></i>Transfer Ownership</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Danger Zone Tab */}
        {activeTab === 'danger' && userRole === 'owner' && (
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="text-base font-semibold text-red-900 mb-2 flex items-center gap-2">
                <i className="ri-delete-bin-line"></i>
                Delete Group
              </h3>
              <p className="text-sm text-red-700 mb-4">
                Once you delete this group, there is no going back. All posts, members, and data will be permanently removed.
              </p>
              <Button
                onClick={handleDelete}
                variant="outline"
                disabled={isSaving}
                className="border-red-500 text-red-600 hover:bg-red-50 whitespace-nowrap"
              >
                <i className="ri-delete-bin-line mr-2"></i>
                Delete This Group
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 mt-6 pt-5 border-t border-gray-200">
        <Button onClick={onClose} variant="outline" disabled={isSaving}>
          Cancel
        </Button>
        {activeTab !== 'danger' && activeTab !== 'members' && (
          <Button onClick={handleSave} variant="primary" disabled={isSaving} className="whitespace-nowrap">
            {isSaving ? (
              <><i className="ri-loader-4-line animate-spin mr-2"></i>Saving...</>
            ) : (
              'Save Changes'
            )}
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default GroupSettingsModal;
